import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Check, Plus, Trash2, X } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardPageFormBody,
  DashboardTableMultiSelectFilter,
  buildDashboardDataTableColumns,
  useDashboardDataTableClientPage,
} from '@shigo-ui/dashboard-ui';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@shigo-ui/ui';

import { DashboardSelectionDialog } from '../../../../../components/DashboardSelectionDialog';
import { countByStatus, manageDialogFetchSize } from '../../../../../lib/clientSelectionList';
import { formatUsd, numberFormat } from '../../../../../lib/format';
import { IntegrationStatus } from '../../../integrations/model';
import { useDealIntegrationsQuery } from '../../hooks';
import {
  type DealFormValue,
  type DealIntegration,
  type DealIntegrationLookupFilter,
} from '../../model';

const lookupFilter: DealIntegrationLookupFilter = {
  searchTerm: '',
  page: 0,
  size: manageDialogFetchSize,
  statuses: [],
};

export function DealIntegrationsFields() {
  'use no memo';
  const { control, setValue } = useFormContext<DealFormValue>();
  const selected = useWatch({ control, name: 'demandIntegration' });
  const [manageOpen, setManageOpen] = React.useState(false);
  const [statuses, setStatuses] = React.useState<IntegrationStatus[]>([IntegrationStatus.Active]);
  const query = useDealIntegrationsQuery(lookupFilter, manageOpen);
  const columns = buildDashboardDataTableColumns<DealIntegration>([
    textColumn('name', 'Name', integrationNameCell, 'w-[280px] max-w-[280px]'),
    textColumn(
      'demandPartner',
      'DP',
      (item) => item.demandPartner?.displayName ?? item.dpDisplayName ?? '-',
    ),
    {
      id: 'duplication',
      header: 'Duplication',
      kind: DashboardDataTableColumnKind.Custom,
      headerClassName: 'w-28 text-center',
      className: 'w-28 text-center',
      cell: (item) => {
        const duplicated = item.duplicateDemandIntegration ?? item.duplication;
        return duplicated ? (
          <Check className="text-success mx-auto h-4 w-4" aria-label="Yes" />
        ) : (
          <X className="text-muted-foreground mx-auto h-4 w-4" aria-label="No" />
        );
      },
    },
    textColumn('revenue', 'Last 7 days revenue', (item) => formatUsd(item.revenueLast7Days)),
    textColumn('revenuePercentage', 'Revenue %', (item) =>
      formatPercentage(item.stats?.global7daysRevenuePct),
    ),
    textColumn('adRequests', 'Last 7 days ad requests', (item) =>
      formatNumber(item.adRequestsLast7Days),
    ),
    textColumn('sellerDomain', 'Seller domain (ASI)', integrationSellerDomain),
    textColumn('integrationType', 'Integration type', (item) => item.integrationType),
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      header: 'Actions',
      cell: (item) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Remove ${integrationName(item)}`}
          onClick={() => {
            setValue(
              'demandIntegration',
              selected.filter((candidate) => candidate.id !== item.id),
              { shouldDirty: true },
            );
          }}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      ),
    },
  ]);

  // Long lists render one page of rows, not every row (keeps tab switches fast).
  const tablePage = useDashboardDataTableClientPage(selected);
  return (
    <DashboardPageFormBody width="lg">
      <DashboardDataTable
        title="Demand integrations"
        tableLabel="Selected demand integrations"
        columns={columns}
        items={tablePage.items}
        pagination={tablePage.pagination}
        getRowKey={(item) => item.id}
        emptyMessage="No demand integrations selected."
        minWidthClassName="min-w-[1560px]"
        toolbar={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setManageOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Manage integrations
            </Button>
            {selected.length ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setValue('demandIntegration', [], { shouldDirty: true });
                }}
              >
                Remove all
              </Button>
            ) : null}
          </>
        }
      />
      <DashboardSelectionDialog
        open={manageOpen}
        title="Manage integrations"
        description="Select demand integrations for this deal."
        items={query.data?.content ?? []}
        selected={selected}
        loading={query.isLoading || query.isFetching}
        error={query.isError}
        getId={(item) => item.id}
        getName={integrationName}
        initialPageSize={5}
        clientFilterKey={statuses.join(',')}
        matches={(item) => statuses.length === 0 || statuses.includes(item.status)}
        columns={[
          textColumn('status', 'Status', (item) => statusLabel(item.status), 'w-32'),
          textColumn('sellerDomain', 'Seller domain (ASI)', integrationSellerDomain),
          textColumn('integrationType', 'Integration type', (item) => item.integrationType),
        ]}
        toolbarAfterSearch={(searchedItems) => {
          const counts = countByStatus(searchedItems);
          return (
            <DashboardTableMultiSelectFilter
              ariaLabel="Filter integrations by status"
              label={statuses.map(statusLabel).join(', ') || 'All statuses'}
              value={statuses}
              options={Object.values(IntegrationStatus).map((status) => ({
                value: status,
                label: statusLabel(status),
                count: counts[status] ?? 0,
              }))}
              onChange={setStatuses}
            />
          );
        }}
        searchLabel="Search integrations"
        loadingMessage="Loading integrations…"
        errorMessage="Integrations could not be loaded."
        emptyMessage="No integrations found."
        minWidthClassName="min-w-[920px]"
        onOpenChange={setManageOpen}
        onConfirm={(items) => {
          setValue('demandIntegration', items, { shouldDirty: true });
        }}
      />
    </DashboardPageFormBody>
  );
}

function textColumn(
  id: string,
  header: string,
  value: (item: DealIntegration) => React.ReactNode,
  className = 'min-w-44',
) {
  return {
    id,
    header,
    kind: DashboardDataTableColumnKind.Text,
    value,
    fallback: '-',
    headerClassName: className,
    className,
  } as const;
}

function integrationName(item: DealIntegration) {
  return item.name;
}

function integrationNameCell(item: DealIntegration) {
  const name = integrationName(item);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block max-w-[248px] truncate text-left whitespace-nowrap">{name}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-80 whitespace-normal">{name}</TooltipContent>
    </Tooltip>
  );
}

function integrationSellerDomain(item: DealIntegration) {
  return item.schainAsi ?? item.sellerDomain ?? '-';
}

function statusLabel(status: string) {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

function formatPercentage(value?: number) {
  if (value === undefined) return '-';
  return numberFormat({ style: 'percent', maximumFractionDigits: 2 }).format(
    Math.abs(value) > 1 ? value / 100 : value,
  );
}

function formatNumber(value?: number) {
  return value === undefined ? '-' : numberFormat().format(value);
}
